import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Avatar,
  Stack,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Pagination,
  Drawer,
  Divider,
  Paper,
  Tooltip,
  Grid,
  CardMedia,
  Rating,
  Badge,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  AccessTime,
  Verified,
  MedicalServices,
  Search,
  FilterList,
  Clear,
  Sort,
  ExpandMore,
  Close,
  Star,
  People,
  Business,
  Map,
  Refresh,
  KeyboardArrowRight,
  Schedule,
  AttachMoney,
  Work,
} from '@mui/icons-material';
import { getClinics, getSpecialties, getServiceProviderTypes } from '../services/openapis';
import BackButton from '../components/BackButton';

const ClinicList = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // State management
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [providerTypes, setProviderTypes] = useState([]);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedProviderType, setSelectedProviderType] = useState('');
  const [clinicTypeFilter, setClinicTypeFilter] = useState('');
  const [consultationFeeFilter, setConsultationFeeFilter] = useState('');
  const [experienceFilter, setExperienceFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [showVerifiedOnly, setShowVerifiedOnly] = useState(false);
  
  // Sorting and pagination
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(12);
  
  // UI state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  // Sort options
  const sortOptions = [
    { value: 'name', label: 'Name (A-Z)' },
    { value: '-name', label: 'Name (Z-A)' },
    { value: 'consultation_fee', label: 'Fee (Low to High)' },
    { value: '-consultation_fee', label: 'Fee (High to Low)' },
    { value: 'experience_years', label: 'Experience (Low to High)' },
    { value: '-experience_years', label: 'Experience (High to Low)' },
    { value: '-created_at', label: 'Newest First' },
    { value: 'created_at', label: 'Oldest First' },
  ];

  // Clinic type filter options
  const clinicTypeOptions = [
    { value: 'single_doctor', label: 'Single Doctor Clinic' },
    { value: 'up_to_3_doctors', label: 'Up to 3 Doctors' },
    { value: 'more_than_3_doctors', label: 'More than 3 Doctors' },
  ];

  // Consultation fee filter options
  const consultationFeeOptions = [
    { value: '0-500', label: '₹0 - ₹500' },
    { value: '501-1000', label: '₹501 - ₹1000' },
    { value: '1001-2000', label: '₹1001 - ₹2000' },
    { value: '2001-5000', label: '₹2001 - ₹5000' },
    { value: '5000+', label: '₹5000+' },
  ];

  // Experience filter options
  const experienceOptions = [
    { value: '0-5', label: '0-5 years' },
    { value: '6-10', label: '6-10 years' },
    { value: '11-15', label: '11-15 years' },
    { value: '16-20', label: '16-20 years' },
    { value: '20+', label: '20+ years' },
  ];

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        
        // Fetch specialties and provider types in parallel
        const [specialtiesData, providerTypesData] = await Promise.all([
          getSpecialties(),
          getServiceProviderTypes()
        ]);
        
        setSpecialties(specialtiesData.results || []);
        setProviderTypes(providerTypesData.results || []);
        
        // Check for URL parameters
        const searchParam = searchParams.get('search');
        const specialtyParam = searchParams.get('specialty');
        const providerTypeParam = searchParams.get('providerType');
        const clinicTypeParam = searchParams.get('clinicType');
        const consultationFeeParam = searchParams.get('consultationFee');
        const experienceParam = searchParams.get('experience');
        const locationParam = searchParams.get('location');
        const sortParam = searchParams.get('sort');
        const pageParam = searchParams.get('page');
        const verifiedParam = searchParams.get('verified');
        
        // Set state from URL parameters
        if (searchParam) setSearchTerm(searchParam);
        if (specialtyParam) setSelectedSpecialty(specialtyParam);
        if (providerTypeParam) setSelectedProviderType(providerTypeParam);
        if (clinicTypeParam) setClinicTypeFilter(clinicTypeParam);
        if (consultationFeeParam) setConsultationFeeFilter(consultationFeeParam);
        if (experienceParam) setExperienceFilter(experienceParam);
        if (locationParam) setLocationFilter(locationParam);
        if (sortParam) setSortBy(sortParam);
        if (pageParam) setCurrentPage(parseInt(pageParam));
        if (verifiedParam) setShowVerifiedOnly(verifiedParam === 'true');
        
        setError(null);
      } catch (err) {
        setError('Failed to fetch initial data. Please try again later.');
        console.error('Error fetching initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [searchParams]);

  // Fetch clinics with filters and pagination using dedicated Clinics API
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        setLoading(true);
        
        // Build filter parameters for dedicated Clinics API
        const filters = {
          page: currentPage,
          pageSize: pageSize,
        };
        
        // Add search query
        if (searchTerm) {
          filters.search = searchTerm;
        }
        
        // Add specialty filter
        if (selectedSpecialty) {
          filters.specialty = selectedSpecialty;
        }
        
        // Add provider type filter
        if (selectedProviderType) {
          filters.providerType = selectedProviderType;
        }
        
        // Add clinic type filter
        if (clinicTypeFilter) {
          filters.clinicType = clinicTypeFilter;
        }
        
        // Add consultation fee filter
        if (consultationFeeFilter) {
          filters.consultationFee = consultationFeeFilter;
        }
        
        // Add experience filter
        if (experienceFilter) {
          filters.experienceYears = experienceFilter;
        }
        
        // Add location filter
        if (locationFilter) {
          filters.location = locationFilter;
        }
        
        // Add sorting
        if (sortBy) {
          filters.ordering = sortBy;
        }
        
        console.log('Fetching clinics with dedicated API:', filters); // Debug log
        
        const response = await getClinics(filters);
        
        // Handle paginated response
        let clinicsData = [];
        let totalCount = 0;
        
        if (response.results) {
          clinicsData = response.results;
          totalCount = response.count || clinicsData.length;
        } else if (Array.isArray(response)) {
          clinicsData = response;
          totalCount = response.length;
        } else {
          throw new Error('Invalid response format from clinics API');
        }
        
        setClinics(clinicsData);
        setTotalPages(Math.ceil(totalCount / pageSize));
        setTotalCount(totalCount);
        setError(null);
        
        console.log('Clinics fetched successfully from dedicated API:', {
          count: clinicsData.length,
          total: totalCount,
          page: currentPage
        });
        
      } catch (err) {
        setError('Failed to fetch clinics. Please try again later.');
        console.error('Error fetching clinics from dedicated API:', err);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchClinics, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, selectedSpecialty, selectedProviderType, clinicTypeFilter, consultationFeeFilter, experienceFilter, locationFilter, showVerifiedOnly, sortBy, currentPage, pageSize, specialties]);

  // Update active filters
  useEffect(() => {
    const filters = [];
    if (searchTerm) filters.push(`Search: "${searchTerm}"`);
    if (selectedSpecialty) {
      const specialty = specialties.find(s => s.id === selectedSpecialty);
      if (specialty) filters.push(`Specialty: ${specialty.name}`);
    }
    if (selectedProviderType) {
      const providerType = providerTypes.find(p => p.id === selectedProviderType);
      if (providerType) filters.push(`Type: ${providerType.name}`);
    }
    if (clinicTypeFilter) {
      const clinicType = clinicTypeOptions.find(c => c.value === clinicTypeFilter);
      if (clinicType) filters.push(`Clinic Type: ${clinicType.label}`);
    }
    if (consultationFeeFilter) {
      const feeOption = consultationFeeOptions.find(f => f.value === consultationFeeFilter);
      if (feeOption) filters.push(`Fee: ${feeOption.label}`);
    }
    if (experienceFilter) {
      const expOption = experienceOptions.find(e => e.value === experienceFilter);
      if (expOption) filters.push(`Experience: ${expOption.label}`);
    }
    if (locationFilter) filters.push(`Location: ${locationFilter}`);
    if (showVerifiedOnly) filters.push('Verified Only');
    
    setActiveFilters(filters);
  }, [searchTerm, selectedSpecialty, selectedProviderType, clinicTypeFilter, consultationFeeFilter, experienceFilter, locationFilter, showVerifiedOnly, specialties, providerTypes]);

  // Update URL parameters
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (searchTerm) params.set('search', searchTerm);
    if (selectedSpecialty) params.set('specialty', selectedSpecialty);
    if (selectedProviderType) params.set('providerType', selectedProviderType);
    if (clinicTypeFilter) params.set('clinicType', clinicTypeFilter);
    if (consultationFeeFilter) params.set('consultationFee', consultationFeeFilter);
    if (experienceFilter) params.set('experience', experienceFilter);
    if (locationFilter) params.set('location', locationFilter);
    if (sortBy !== 'name') params.set('sort', sortBy);
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (showVerifiedOnly) params.set('verified', 'true');
    
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedSpecialty, selectedProviderType, clinicTypeFilter, consultationFeeFilter, experienceFilter, locationFilter, sortBy, currentPage, showVerifiedOnly, setSearchParams]);

  // Event handlers
  const handleClinicClick = (clinicId) => {
    navigate(`/hospitals/${clinicId}`);
  };

  const handleViewDoctors = (clinicId) => {
    navigate(`/find-doctors?provider=${clinicId}`);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedSpecialty('');
    setSelectedProviderType('');
    setClinicTypeFilter('');
    setConsultationFeeFilter('');
    setExperienceFilter('');
    setLocationFilter('');
    setShowVerifiedOnly(false);
    setSortBy('name');
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Helper functions
  const getClinicRating = (clinic) => {
    if (clinic.rating) return clinic.rating;
    const baseRating = 4.2;
    const typeBonus = clinic.provider_type_name?.includes('Clinic') ? 0.2 : 0.1;
    return Math.round((baseRating + typeBonus + Math.random() * 0.5) * 10) / 10;
  };

  const getReviewCount = (clinic) => {
    if (clinic.review_count) return clinic.review_count;
    return Math.floor(Math.random() * 150 + 30);
  };

  const getClinicStatus = (clinic) => {
    if (clinic.status) return clinic.status;
    return Math.random() > 0.3 ? 'Open Now' : 'Closed';
  };

  const getConsultationFee = (clinic) => {
    if (clinic.consultation_fee) return clinic.consultation_fee;
    return Math.floor(Math.random() * 2000 + 500);
  };

  const getExperienceYears = (clinic) => {
    if (clinic.experience_years) return clinic.experience_years;
    return Math.floor(Math.random() * 20 + 5);
  };

  const getDoctorCount = (clinic) => {
    if (clinic.doctor_count) return clinic.doctor_count;
    return Math.floor(Math.random() * 5 + 1);
  };

  const getClinicType = (clinic) => {
    const doctorCount = getDoctorCount(clinic);
    if (doctorCount === 1) return 'Single Doctor Clinic';
    if (doctorCount <= 3) return 'Up to 3 Doctors';
    return 'More than 3 Doctors';
  };

  // 1. Add LeftSidebarFilters for desktop (web)
  const LeftSidebarFilters = () => (
    <Paper 
      elevation={0}
      sx={{ 
        p: 3, 
        height: 'fit-content', 
        position: 'sticky', 
        top: 20,
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h6" 
          sx={{ fontWeight: 700 }}
        >
          Filters
        </Typography>
        <Button
          size="small"
          onClick={handleClearFilters}
          startIcon={<Clear />}
          disabled={activeFilters.length === 0}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: activeFilters.length > 0 ? '#ffebee' : 'transparent',
            color: activeFilters.length > 0 ? '#d32f2f' : 'text.secondary',
            '&:hover': {
              bgcolor: activeFilters.length > 0 ? '#ffcdd2' : 'transparent',
            }
          }}
        >
          Clear All
        </Button>
      </Box>
      <Divider sx={{ mb: 3, opacity: 0.6 }} />
      {/* Search */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Search
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search clinics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#666' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Sort By */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Sort By
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Specialty */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Specialty
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedSpecialty}
            onChange={(e) => setSelectedSpecialty(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">All Specialties</MenuItem>
            {specialties.map((specialty) => (
              <MenuItem key={specialty.id} value={specialty.id}>
                {specialty.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Provider Type */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Clinic Type
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={selectedProviderType}
            onChange={(e) => setSelectedProviderType(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">All Types</MenuItem>
            {providerTypes
              .filter(type => type.name?.toLowerCase().includes('clinic'))
              .map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      {/* Clinic Type */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Doctor Count
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={clinicTypeFilter}
            onChange={(e) => setClinicTypeFilter(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">Any Size</MenuItem>
            {clinicTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Consultation Fee */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Consultation Fee
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={consultationFeeFilter}
            onChange={(e) => setConsultationFeeFilter(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">Any Fee</MenuItem>
            {consultationFeeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Experience */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Experience
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={experienceFilter}
            onChange={(e) => setExperienceFilter(e.target.value)}
            sx={{
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0,0,0,0.12)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              },
            }}
          >
            <MenuItem value="">Any Experience</MenuItem>
            {experienceOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Location */}
      <Box mb={3}>
        <Typography variant="subtitle2" fontWeight={600} mb={1.5} color="text.primary">
          Location
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Enter city or area"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#1976d2',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1976d2',
              },
            },
          }}
        />
      </Box>
      {/* Verified Only Toggle */}
      <Box mb={3}>
        <FormControlLabel
          control={
            <Switch
              checked={showVerifiedOnly}
              onChange={(e) => setShowVerifiedOnly(e.target.checked)}
            />
          }
          label="Verified clinics only"
        />
      </Box>
    </Paper>
  );

  // 2. Update FilterDrawer to be full width on mobile
  const FilterDrawer = () => (
    <Drawer
      anchor="right"
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 320 }, maxWidth: '100vw' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1, pb: 0 }}>
        <BackButton onClick={() => setFilterDrawerOpen(false)} size="medium" color="primary" />
      </Box>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <Stack spacing={3}>
          {/* Search */}
          <TextField
            fullWidth
            label="Search clinics"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          {/* Specialty Filter */}
          <FormControl fullWidth>
            <InputLabel>Specialty</InputLabel>
            <Select
              value={selectedSpecialty}
              onChange={(e) => setSelectedSpecialty(e.target.value)}
              label="Specialty"
            >
              <MenuItem value="">All Specialties</MenuItem>
              {specialties.map((specialty) => (
                <MenuItem key={specialty.id} value={specialty.id}>
                  {specialty.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Provider Type Filter */}
          <FormControl fullWidth>
            <InputLabel>Clinic Type</InputLabel>
            <Select
              value={selectedProviderType}
              onChange={(e) => setSelectedProviderType(e.target.value)}
              label="Clinic Type"
            >
              <MenuItem value="">All Types</MenuItem>
              {providerTypes
                .filter(type => type.name?.toLowerCase().includes('clinic'))
                .map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {/* Clinic Type Filter */}
          <FormControl fullWidth>
            <InputLabel>Doctor Count</InputLabel>
            <Select
              value={clinicTypeFilter}
              onChange={(e) => setClinicTypeFilter(e.target.value)}
              label="Doctor Count"
            >
              <MenuItem value="">Any Size</MenuItem>
              {clinicTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Consultation Fee Filter */}
          <FormControl fullWidth>
            <InputLabel>Consultation Fee</InputLabel>
            <Select
              value={consultationFeeFilter}
              onChange={(e) => setConsultationFeeFilter(e.target.value)}
              label="Consultation Fee"
            >
              <MenuItem value="">Any Fee</MenuItem>
              {consultationFeeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Experience Filter */}
          <FormControl fullWidth>
            <InputLabel>Experience</InputLabel>
            <Select
              value={experienceFilter}
              onChange={(e) => setExperienceFilter(e.target.value)}
              label="Experience"
            >
              <MenuItem value="">Any Experience</MenuItem>
              {experienceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Location Filter */}
          <TextField
            fullWidth
            label="Location"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            placeholder="Enter city or area"
          />
          {/* Verified Only Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showVerifiedOnly}
                onChange={(e) => setShowVerifiedOnly(e.target.checked)}
              />
            }
            label="Verified clinics only"
          />
          <Divider />
          {/* Clear Filters Button */}
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<Clear />}
            fullWidth
          >
            Clear All Filters
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );

  // Clinic Card Component
  const ClinicCard = ({ clinic }) => (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
      onClick={() => handleClinicClick(clinic.id)}
    >
      {/* Clinic Image or Placeholder */}
      <CardMedia
        component="img"
        height="140"
        image={clinic.image || '/api/placeholder/400/140'}
        alt={clinic.name}
        sx={{ objectFit: 'cover' }}
      />

      <CardContent sx={{ p: { xs: 2, md: 2.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header - Clinic Name & Type */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: { xs: 1.5, md: 2 } }}>
          <Avatar
            sx={{
              width: { xs: 45, md: 55 },
              height: { xs: 45, md: 55 },
              bgcolor: '#00acc1',
              border: '2px solid #fff',
              boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
            }}
          >
            <MedicalServices sx={{ fontSize: { xs: 20, md: 26 } }} />
          </Avatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.2,
                mb: 0.3,
                color: '#1a1a1a',
              }}
            >
              {clinic.name}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                fontSize: { xs: '0.7rem', md: '0.75rem' },
                lineHeight: 1.1,
                color: '#00acc1',
              }}
            >
              {getClinicType(clinic)}
            </Typography>
          </Box>

          {/* Verification Badge */}
          {clinic.verified && (
            <Tooltip title="Verified Clinic">
              <Verified sx={{ color: 'success.main', fontSize: 20 }} />
            </Tooltip>
          )}
        </Box>

        {/* Rating and Reviews */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Rating value={getClinicRating(clinic)} precision={0.1} size="small" readOnly />
          <Typography variant="body2" color="text.secondary">
            ({getReviewCount(clinic)} reviews)
          </Typography>
        </Box>

        {/* Location & Distance */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
          <LocationOn sx={{ color: 'text.secondary', mt: 0.5, fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, flex: 1 }}>
            {clinic.address || 'Address not available'}
          </Typography>
          {clinic.distance_km && (
            <Chip
              label={`${clinic.distance_km} km`}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 600,
              }}
            />
          )}
        </Box>

        {/* Clinic Stats */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {getDoctorCount(clinic)} doctors
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Work sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {getExperienceYears(clinic)} years
            </Typography>
          </Box>
        </Box>

        {/* Consultation Fee */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AttachMoney sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
            ₹{getConsultationFee(clinic)} consultation fee
          </Typography>
        </Box>

        {/* Specialties */}
        {clinic.specialties && clinic.specialties.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Specialties:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {clinic.specialties.slice(0, 3).map((specialty, index) => (
                <Chip
                  key={index}
                  label={specialty.name}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {clinic.specialties.length > 3 && (
                <Chip
                  label={`+${clinic.specialties.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccessTime sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
            {getClinicStatus(clinic)}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              handleClinicClick(clinic.id);
            }}
          >
            View Details
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDoctors(clinic.id);
            }}
          >
            Doctors
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading && clinics.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mb: '100px',py: 4, bgcolor: theme.palette.background.default }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Clinics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find and compare clinics in your area
          </Typography>
        </Box>
        {isMobile && (
          <IconButton
            onClick={() => setFilterDrawerOpen(true)}
            sx={{
              border: 2,
              borderColor: '#1976d2',
              bgcolor: 'white',
              boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)',
              '&:hover': {
                bgcolor: '#f3f8ff',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
              position: 'relative',
              zIndex: 1,
              ml: 2
            }}
          >
            <FilterList sx={{ color: '#1976d2' }} />
          </IconButton>
        )}
      </Box>
      {/* Main Content Grid: Sidebar + Results */}
      <Grid container spacing={4}>
        {/* Left Sidebar - Filters (Desktop) */}
        {!isMobile && (
          <Grid item xs={12} md={3}>
            <LeftSidebarFilters />
          </Grid>
        )}
        {/* Right Side - Results */}
        <Grid item xs={12} md={9}>
          {/* Active Filters Summary */}
          {activeFilters.length > 0 && (
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                mb: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
                border: '1px solid rgba(76, 175, 80, 0.2)',
                boxShadow: '0 4px 20px rgba(76, 175, 80, 0.1)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                  Applied Filters:
                </Typography>
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<Clear />}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: 'white',
                    color: '#d32f2f',
                    '&:hover': {
                      bgcolor: '#ffebee',
                    }
                  }}
                >
                  Clear All
                </Button>
              </Box>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {activeFilters.map((filter, index) => (
                  <Chip
                    key={index}
                    label={filter}
                    onDelete={() => {
                      // Handle individual filter removal
                      if (filter.startsWith('Search:')) setSearchTerm('');
                      if (filter.startsWith('Specialty:')) setSelectedSpecialty('');
                      if (filter.startsWith('Type:')) setSelectedProviderType('');
                      if (filter.startsWith('Clinic Type:')) setClinicTypeFilter('');
                      if (filter.startsWith('Fee:')) setConsultationFeeFilter('');
                      if (filter.startsWith('Experience:')) setExperienceFilter('');
                      if (filter.startsWith('Location:')) setLocationFilter('');
                      if (filter === 'Verified Only') setShowVerifiedOnly(false);
                    }}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      bgcolor: 'white',
                      color: '#2e7d32',
                      border: '1px solid rgba(76, 175, 80, 0.3)',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#f1f8e9',
                      }
                    }}
                    deleteIcon={<Close sx={{ fontSize: 16 }} />}
                  />
                ))}
              </Box>
            </Paper>
          )}
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {/* Results Summary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {clinics.length} of {totalCount} clinics
            </Typography>
            {loading && <CircularProgress size={20} />}
          </Box>
          {/* Clinics Grid */}
          <Grid container spacing={3}>
            {clinics.map((clinic) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={clinic.id}>
                <ClinicCard clinic={clinic} />
              </Grid>
            ))}
          </Grid>
          {/* No Results */}
          {!loading && clinics.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <MedicalServices sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No clinics found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try adjusting your search criteria or filters
              </Typography>
              <Button variant="outlined" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            </Box>
          )}
          {/* Pagination */}
          {totalPages > 1 && totalCount > clinics.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      {/* Filter Drawer (Mobile) */}
      {isMobile && <FilterDrawer />}
    </Container>
  );
};

export default ClinicList; 